/**
 * Admin: configurare modul Fidelizare.
 */
import { $, getBucharestDateKey } from '../utils/helpers.js?v=83.21';
import { hasFeature, featureLockedMessage } from '../config/plans.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';
import { validateFileUpload } from '../utils/validation.js?v=83.21';
import { getCurrentUser } from '../services/auth.js?v=83.21';
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=83.21';
import {
  getLoyaltySettingsBackend,
  saveLoyaltySettingsBackend
} from '../services/backend.js?v=83.21';
import { uploadFile } from '../services/storage.js?v=83.21';
import { refreshAdminSummaries } from './admin.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';

export function invalidateLoyaltySettingsCache() {
  import('../services/loyalty.js?v=83.21')
    .then((m) => {
      if (typeof m.clearLoyaltySettingsLoadCache === 'function') {
        m.clearLoyaltySettingsLoadCache();
      }
    })
    .catch(() => {});
}

export const DEFAULT_LOYALTY_TITLE = 'Card de fidelitate';
export const DEFAULT_LOYALTY_DESCRIPTION =
  'Scanează bonul la fiecare vizită și primești recompensa.';
export const DEFAULT_LOYALTY_BUTTON = 'Cardul meu de fidelitate';
export const DEFAULT_LOYALTY_REQUIRED = 6;
export const DEFAULT_LOYALTY_REWARD_TEXT = 'o cafea gratuită';

function syncRequiredChips() {
  const val = String($('loyaltyRequiredPurchases')?.value || '');
  let matched = false;
  document.querySelectorAll('#loyaltyRequiredChips [data-loy-req]').forEach((b) => {
    const on = b.dataset.loyReq === val;
    if (on) matched = true;
    b.classList.toggle('is-on', on);
  });
  const other = $('loyaltyRequiredOther');
  if (other) {
    if (matched) {
      if (document.activeElement !== other) other.value = '';
      other.classList.remove('is-on');
    } else {
      if (document.activeElement !== other) other.value = val;
      other.classList.toggle('is-on', !!val);
    }
  }
}

function syncPeriodNote() {
  const endEl = $('loyaltyEndDate');
  const startEl = $('loyaltyStartDate');
  if (!endEl) return;
  let note = $('loyaltyPeriodNote');
  if (!note) {
    note = document.createElement('p');
    note.id = 'loyaltyPeriodNote';
    note.className = 'loy-period-note';
    note.setAttribute('role', 'status');
    const anchor = endEl.closest('details') || endEl.parentElement;
    anchor?.parentElement?.insertBefore(note, anchor);
  }
  const today = getBucharestDateKey();
  const end = String(endEl.value || '').trim();
  const start = String(startEl?.value || '').trim();
  let msg = '';
  if (end && today > end) {
    msg = `⚠️ Perioada s-a încheiat pe ${end.split('-').reverse().join('.')} — clienții nu mai văd cardul. Schimbă sau șterge data de final din „Texte și perioadă”.`;
  } else if (start && today < start) {
    msg = `ℹ️ Programul pornește pe ${start.split('-').reverse().join('.')} — până atunci clienții nu văd cardul.`;
  }
  note.textContent = msg;
  note.hidden = !msg;
}

const LOYALTY_PRESETS = [
  { label: '☕ Cafea gratuită', req: 6, reward: 'o cafea gratuită' },
  { label: '🍰 Desert gratuit', req: 8, reward: 'un desert din partea casei' },
  { label: '🎁 Produs gratuit', req: 10, reward: 'un produs gratuit la alegere' },
  { label: '% Reducere 20%', req: 5, reward: '20% reducere la următoarea vizită' }
];

/** Idei rapide: un tap completează numărul de bonuri și recompensa. */
function mountLoyaltyPresets() {
  const reward = $('loyaltyRewardText');
  if (!reward || document.getElementById('loyaltyPresetRow')) return;
  const row = document.createElement('div');
  row.id = 'loyaltyPresetRow';
  row.className = 'loy-presets';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Idei rapide de recompensă');
  LOYALTY_PRESETS.forEach((p) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'loy-preset';
    b.textContent = p.label;
    b.addEventListener('click', () => {
      const hidden = $('loyaltyRequiredPurchases');
      if (hidden) hidden.value = String(p.req);
      reward.value = p.reward;
      syncRequiredChips();
      setLoyaltyDirtyState(true);
    });
    row.appendChild(b);
  });
  const label = document.querySelector('label[for="loyaltyRewardText"]');
  (label || reward).parentElement.insertBefore(row, label || reward);
}

function syncCuiNote() {
  const note = $('loyaltyCuiNote');
  if (!note) return;
  const cui = String(getCurrentSettings()?.cui || '').replace(/\D/g, '');
  note.textContent = cui
    ? `Se acceptă doar bonuri cu CUI ${cui}.`
    : '⚠️ Contul nu are un CUI valid — bonurile nu vor fi verificate după firmă (doar dată și unicitate).';
}

let localPromoImage = null;
let hasUnsavedLoyaltyChanges = false;

export function loyaltyFormHasUnsavedChanges() {
  return hasUnsavedLoyaltyChanges === true && loyaltyFormLoaded === true;
}

function setLoyaltyDirtyState(dirty) {
  hasUnsavedLoyaltyChanges = !!dirty;
  const badge = document.getElementById('loyaltyDirtyBadge');
  if (badge) badge.style.display = hasUnsavedLoyaltyChanges ? '' : 'none';
}

export function syncLoyaltyFieldsVisibility() {
  const fields = $('loyaltyModuleFields');
  if (fields) {
    fields.style.display = 'block';
  }
}

function renderLoyaltyPromoPreview(url) {
  const preview = $('loyaltyPromoPreview');
  if (!preview) return;
  const safe = sanitizeImageSrc(url || '');
  if (safe) {
    preview.innerHTML = '';
    const img = document.createElement('img');
    img.src = safe;
    img.alt = 'Imagine promo fidelizare';
    img.className = 'loyalty-promo-preview__img';
    preview.appendChild(img);
    preview.style.display = '';
  } else {
    preview.innerHTML = '<p class="smallnote">Nicio imagine încărcată.</p>';
    preview.style.display = '';
  }
}

export function bindLoyaltyAdminControls() {
  [
    'loyaltyTitle',
    'loyaltyDescription',
    'loyaltyButtonText',
    'loyaltyRequiredPurchases',
    'loyaltyRewardText',
    'loyaltyStartDate',
    'loyaltyEndDate',
    'loyaltyMinAmount'
  ].forEach((id) => {
    const el = $(id);
    if (el && !el.dataset.loyaltyFieldBound) {
      el.dataset.loyaltyFieldBound = '1';
      el.addEventListener('input', () => setLoyaltyDirtyState(true));
    }
  });

  document.querySelectorAll('#loyaltyRequiredChips [data-loy-req]').forEach((b) => {
    if (b.dataset.loyBound) return;
    b.dataset.loyBound = '1';
    b.addEventListener('click', () => {
      const inp = $('loyaltyRequiredPurchases');
      if (inp) inp.value = b.dataset.loyReq;
      syncRequiredChips();
      setLoyaltyDirtyState(true);
    });
  });
  const otherInp = $('loyaltyRequiredOther');
  if (otherInp && !otherInp.dataset.loyChipSync) {
    otherInp.dataset.loyChipSync = '1';
    otherInp.addEventListener('input', () => {
      let n = parseInt(otherInp.value, 10);
      const hidden = $('loyaltyRequiredPurchases');
      if (Number.isFinite(n) && (n < 1 || n > 99)) {
        n = Math.max(1, Math.min(99, n));
        otherInp.value = String(n);
      }
      if (hidden && Number.isFinite(n) && n >= 1 && n <= 99) hidden.value = String(n);
      syncRequiredChips();
      setLoyaltyDirtyState(true);
    });
  }
  mountLoyaltyPresets();
  syncRequiredChips();
  syncCuiNote();
  syncPeriodNote();
  ['loyaltyStartDate', 'loyaltyEndDate'].forEach((id) => {
    const d = $(id);
    if (d && !d.dataset.loyPeriodBound) {
      d.dataset.loyPeriodBound = '1';
      d.addEventListener('input', syncPeriodNote);
      d.addEventListener('change', syncPeriodNote);
    }
  });

  const uploadEl = $('loyaltyPromoUpload');
  if (!uploadEl || uploadEl.dataset.boundLoyaltyPromo === '1') return;
  uploadEl.dataset.boundLoyaltyPromo = '1';

  const addBtn = $('loyaltyPromoAddBtn');
  if (addBtn && !addBtn.dataset.loyaltyAddBound) {
    addBtn.dataset.loyaltyAddBound = '1';
    addBtn.addEventListener('click', () => uploadEl.click());
  }

  const removeBtn = $('loyaltyPromoRemoveBtn');
  if (removeBtn && !removeBtn.dataset.loyaltyRemoveBound) {
    removeBtn.dataset.loyaltyRemoveBound = '1';
    removeBtn.addEventListener('click', () => {
      if (localPromoImage?.previewUrl) {
        try {
          URL.revokeObjectURL(localPromoImage.previewUrl);
        } catch (_) {
          /* ignore */
        }
      }
      localPromoImage = null;
      renderLoyaltyPromoPreview('');
      setLoyaltyDirtyState(true);
    });
  }

  uploadEl.addEventListener('change', () => {
    const file = uploadEl.files?.[0];
    uploadEl.value = '';
    if (!file) return;
    const fileValidation = validateFileUpload(file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imagine promo fidelizare'
    });
    if (!fileValidation.valid) {
      showSnackbar(fileValidation.message, 'error');
      return;
    }
    if (localPromoImage?.previewUrl) {
      try {
        URL.revokeObjectURL(localPromoImage.previewUrl);
      } catch (_) {
        /* ignore */
      }
    }
    localPromoImage = {
      type: 'local',
      file,
      previewUrl: URL.createObjectURL(file)
    };
    renderLoyaltyPromoPreview(localPromoImage.previewUrl);
    setLoyaltyDirtyState(true);
  });
}

let loyaltyFormLoaded = false;

/* ---------- Modul Clasament ---------- */
function getLoyaltyMode() {
  return $('loyaltyMode')?.value === 'ranking' ? 'ranking' : 'stamps';
}

function syncLoyaltyModeUI() {
  const mode = getLoyaltyMode();
  const st = getCurrentSettings() || {};
  document.querySelectorAll('#loyaltyModeChips [data-loy-mode]').forEach((b) => {
    const on = b.dataset.loyMode === mode;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
    const need = b.dataset.loyMode === 'ranking' ? 'ranking' : 'loyalty';
    b.classList.toggle('is-locked', !hasFeature(st, need));
  });
  const rank = $('loyaltyRankFields');
  const stamps = $('loyaltyStampsFields');
  if (rank) rank.hidden = mode !== 'ranking';
  if (stamps) stamps.hidden = mode === 'ranking';
  if ($('loyaltyRankEndDate') && $('loyaltyEndDate')) $('loyaltyRankEndDate').value = $('loyaltyEndDate').value;
}

let loyaltyModeBound = false;
function bindLoyaltyModeControls() {
  if (loyaltyModeBound) return;
  loyaltyModeBound = true;
  document.getElementById('loyaltyModeChips')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-loy-mode]');
    if (!b || !$('loyaltyMode')) return;
    const need = b.dataset.loyMode === 'ranking' ? 'ranking' : 'loyalty';
    if (!hasFeature(getCurrentSettings() || {}, need)) {
      showSnackbar(`🔒 ${featureLockedMessage(need)}`, 'info');
      return;
    }
    $('loyaltyMode').value = b.dataset.loyMode;
    syncLoyaltyModeUI();
    setLoyaltyDirtyState(true);
  });
  ['loyaltyRankPrize1', 'loyaltyRankPrize2', 'loyaltyRankPrize3'].forEach((id) => {
    $(id)?.addEventListener('input', () => setLoyaltyDirtyState(true));
  });
  $('loyaltyRankEndDate')?.addEventListener('input', () => {
    if ($('loyaltyEndDate')) $('loyaltyEndDate').value = $('loyaltyRankEndDate').value;
    setLoyaltyDirtyState(true);
  });
  $('loyaltyEndDate')?.addEventListener('input', () => {
    if ($('loyaltyRankEndDate')) $('loyaltyRankEndDate').value = $('loyaltyEndDate').value;
  });
  $('loyaltyRankViewBtn')?.addEventListener('click', openLoyaltyRankingModal);
}

async function openLoyaltyRankingModal() {
  const modal = document.getElementById('loyaltyMembersModal');
  const list = document.getElementById('loyaltyMembersModalList');
  if (!modal || !list) return;
  const title = modal.querySelector('h2');
  if (title) title.textContent = 'Clasament — cele mai multe vizite';
  const sub = document.getElementById('loyaltyMembersSub');
  if (sub) sub.textContent = '';
  const filters = document.getElementById('loyaltyMembersFilters');
  if (filters) filters.hidden = true;
  const actions = modal.querySelector('.sjc-actions');
  if (actions) actions.hidden = true;
  modal.querySelector('.sjc-sheet')?.classList.remove('is-compact');
  list.innerHTML = '<div class="dashboard-empty-state">Se încarcă…</div>';
  modal.classList.add('active');
  try {
    const { getLoyaltyRankingBackend } = await import('../services/backend.js?v=83.21');
    const r = (await getLoyaltyRankingBackend({})) || {};
    const prizes = Array.isArray(r.rankPrizes) ? r.rankPrizes : [];
    const medals = ['🥇', '🥈', '🥉'];
    if (!r.top || !r.top.length) {
      list.innerHTML = '<div class="dashboard-empty-state">Încă nu are nimeni puncte.</div>';
      return;
    }
    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    list.innerHTML =
      `<p class="smallnote" style="margin:0 0 10px;">${r.total} participanți${r.endDate ? ` · se încheie pe ${esc(r.endDate.split('-').reverse().join('.'))}` : ''}. La egalitate e mai sus cine a ajuns primul la acel număr.</p>` +
      r.top.map((t) => `<div class="loy-rank-admin-row${t.position <= 3 ? ' is-top' : ''}">
        <span class="loy-rank-admin-pos">${t.position <= 3 ? medals[t.position - 1] : `${t.position}.`}</span>
        <span class="loy-rank-admin-name">${esc(t.name)}${t.position <= prizes.length ? `<small>${esc(prizes[t.position - 1])}</small>` : ''}</span>
        <span class="loy-rank-admin-pts">${t.points} ${t.points === 1 ? 'vizită' : 'vizite'}</span></div>`).join('');
  } catch (e) {
    list.innerHTML = `<div class="dashboard-empty-state">${(e && e.message) || 'Nu am putut încărca clasamentul.'}</div>`;
  }
}

export async function loadLoyaltySettingsIntoForm() {
  try {
    const res = await getLoyaltySettingsBackend();
    const s = res?.settings || {};
    applyLoyaltySettingsToForm(s);
    loyaltyFormLoaded = true;
    const currentSettings = getCurrentSettings();
    if (currentSettings) {
      currentSettings.loyaltySettings = s;
      setCurrentSettings(currentSettings);
    }
  } catch (e) {
    logger.warn('[loadLoyaltySettingsIntoForm]', e);
    // NU completăm cu valori implicite — la salvare ar suprascrie setările reale.
    loyaltyFormLoaded = false;
    showSnackbar('Nu am putut încărca setările de fidelizare. Închide și redeschide fereastra.', 'error');
  }
}

export function applyLoyaltySettingsToForm(s) {
  if ($('loyaltyTitle')) {
    $('loyaltyTitle').value = String(s.title || '').trim() || DEFAULT_LOYALTY_TITLE;
  }
  if ($('loyaltyDescription')) {
    $('loyaltyDescription').value =
      String(s.description || '').trim() || DEFAULT_LOYALTY_DESCRIPTION;
  }
  if ($('loyaltyButtonText')) {
    $('loyaltyButtonText').value =
      String(s.buttonText || '').trim() || DEFAULT_LOYALTY_BUTTON;
  }
  if ($('loyaltyRequiredPurchases')) {
    const n = parseInt(s.requiredPurchases, 10);
    $('loyaltyRequiredPurchases').value = Number.isFinite(n) && n >= 1 ? n : DEFAULT_LOYALTY_REQUIRED;
  }
  if ($('loyaltyRewardText')) {
    $('loyaltyRewardText').value =
      String(s.rewardText || '').trim() || DEFAULT_LOYALTY_REWARD_TEXT;
  }
  if ($('loyaltyMinAmount')) {
    const mn = Number(s.minAmount) || 0;
    $('loyaltyMinAmount').value = mn > 0 ? String(mn) : '';
  }
  if ($('loyaltyStartDate')) {
    $('loyaltyStartDate').value = String(s.startDate || '').trim();
  }
  if ($('loyaltyEndDate')) {
    $('loyaltyEndDate').value = String(s.endDate || '').trim();
  }
  if ($('loyaltyMode')) $('loyaltyMode').value = s.mode === 'ranking' ? 'ranking' : 'stamps';
  const rp = Array.isArray(s.rankPrizes) ? s.rankPrizes : [];
  ['loyaltyRankPrize1', 'loyaltyRankPrize2', 'loyaltyRankPrize3'].forEach((id, i) => {
    if ($(id)) $(id).value = String(rp[i] || '');
  });
  bindLoyaltyModeControls();
  syncLoyaltyModeUI();
  const url = String(s.promoImageUrl || '').trim();
  localPromoImage = url ? { type: 'remote', url } : null;
  renderLoyaltyPromoPreview(url);
  bindLoyaltyAdminControls();
  syncLoyaltyFieldsVisibility();
  setLoyaltyDirtyState(false);
  import('./mod-client-modules.js?v=83.21')
    .then((m) => m.updateModClientModulesUI?.())
    .catch(() => {});
}

export async function saveLoyaltySettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  if (!loyaltyFormLoaded) {
    throw new Error('Setările de fidelizare nu s-au încărcat încă. Redeschide fereastra și încearcă din nou.');
  }

  const active = getCurrentSettings()?.loyaltySettings?.active === true;
  const title =
    String($('loyaltyTitle')?.value || '').trim().slice(0, 80) || DEFAULT_LOYALTY_TITLE;
  const description =
    String($('loyaltyDescription')?.value || '').trim().slice(0, 500) ||
    DEFAULT_LOYALTY_DESCRIPTION;
  const buttonText =
    String($('loyaltyButtonText')?.value || '').trim().slice(0, 60) || DEFAULT_LOYALTY_BUTTON;
  const requiredRaw = parseInt($('loyaltyRequiredPurchases')?.value, 10);
  const requiredPurchases =
    Number.isFinite(requiredRaw) && requiredRaw >= 1 && requiredRaw <= 99
      ? requiredRaw
      : DEFAULT_LOYALTY_REQUIRED;
  const rewardText =
    String($('loyaltyRewardText')?.value || '').trim().slice(0, 300) ||
    DEFAULT_LOYALTY_REWARD_TEXT;
  const minAmount = Math.max(0, Math.min(10000, Number($('loyaltyMinAmount')?.value) || 0));
  const startDate = String($('loyaltyStartDate')?.value || '').trim();
  const endDate = String($('loyaltyEndDate')?.value || '').trim();
  if (startDate && endDate && startDate > endDate) {
    throw new Error('Data de sfârșit trebuie să fie după data de început.');
  }
  const mode = getLoyaltyMode();
  const rankPrizes = ['loyaltyRankPrize1', 'loyaltyRankPrize2', 'loyaltyRankPrize3']
    .map((id) => String($(id)?.value || '').trim().slice(0, 80))
    .filter(Boolean);
  if (mode === 'ranking') {
    if (!rankPrizes.length) throw new Error('Scrie cel puțin premiul pentru locul 1.');
    if (!endDate) throw new Error('Alege data la care se stabilesc câștigătorii.');
  }

  let promoImageUrl = null;
  if (localPromoImage?.type === 'local' && localPromoImage.file) {
    const validation = validateFileUpload(localPromoImage.file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imagine promo fidelizare'
    });
    if (!validation.valid) {
      throw new Error(validation.message);
    }
    promoImageUrl = await uploadFile(uid, 'loyalty-promo', localPromoImage.file);
  } else if (localPromoImage?.type === 'remote' && localPromoImage.url) {
    promoImageUrl = localPromoImage.url;
  }

  const payload = {
    active,
    title,
    description,
    buttonText,
    requiredPurchases,
    rewardText,
    promoImageUrl: promoImageUrl || null,
    startDate,
    endDate,
    minAmount,
    mode,
    rankPrizes
  };

  invalidateLoyaltySettingsCache();
  const saved = await saveLoyaltySettingsBackend(payload);
  const mergedSettings = saved?.settings || payload;

  const currentSettings = getCurrentSettings();
  if (currentSettings) {
    currentSettings.loyaltySettings = mergedSettings;
    setCurrentSettings(currentSettings);
  }

  if (localPromoImage?.type === 'local' && localPromoImage.previewUrl) {
    try {
      URL.revokeObjectURL(localPromoImage.previewUrl);
    } catch (_) {
      /* ignore */
    }
  }
  if (promoImageUrl) {
    localPromoImage = { type: 'remote', url: promoImageUrl };
  } else if (!localPromoImage) {
    localPromoImage = null;
  }
  setLoyaltyDirtyState(false);
  syncLoyaltyFieldsVisibility();
  refreshAdminSummaries();

  const { applyClientUI } = await import('./slideshow.js?v=83.21');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Setările de fidelizare au fost salvate.', 'success');
  }
}
